/**
 * File: CareerHighlight.jsx
 * Path: /src/components/about/CareerHighlight.jsx
 * Description: Component for the About page that highlights career opportunities
 * Key Features: Visual banner with call-to-action for the Careers page
 */

import React from 'react';
import { motion } from 'framer-motion';
import Button from '../common/Button';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';

const CareerHighlight = () => {
  const [ref, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <section ref={ref} className="py-16 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute left-0 top-0 h-full w-1/2 bg-primary-50 skew-x-12 -ml-32 z-0"></div>
      
      <div className="container-padding mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.7 }}
          >
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <div className="aspect-video bg-gradient-to-br from-primary-700 to-secondary-700 p-6">
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-0 left-0 w-full h-full">
                    {/* Abstract circuit pattern */}
                    <svg width="100%" height="100%" viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
                      <g fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M769.5,591.5 L417.5,591.5 L417.5,196.5 L365.5,196.5 L365.5,591.5 L57.5,591.5" stroke="rgba(255,255,255,0.3)" />
                        <path d="M627.5,358.5 L627.5,520.5 L493.5,520.5 L493.5,421.5 L549.5,421.5" stroke="rgba(255,255,255,0.4)" />
                        <path d="M595.5,308.5 L696.5,308.5 L696.5,224.5 L492.5,224.5 L492.5,87.5 L696.5,87.5" stroke="rgba(255,255,255,0.25)" />
                        <path d="M44.5,358.5 L44.5,144.5 L200.5,144.5 L200.5,242.5 L154.5,242.5" stroke="rgba(255,255,255,0.3)" />
                      </g>
                    </svg>
                  </div>
                </div>
                
                <div className="text-center text-white p-6 relative z-10">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h3 className="text-2xl font-bold mb-2">Join Our Growing Team</h3>
                  <p className="mb-4">Be part of the future of legal technology</p>
                </div>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Build the Future of <span className="gradient-text">Legal Technology</span> with Us
            </h2>
            <p className="text-dark-600 mb-6 text-lg">
              At Veritect.ai, we're looking for passionate individuals across various disciplines who want to 
              make a significant impact on the legal industry through cutting-edge AI innovation.
            </p>
            <div className="space-y-4 mb-8">
              <div className="flex items-start">
                <div className="mr-4 text-primary-600 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold">Competitive Compensation</h4>
                  <p className="text-dark-500">Attractive salaries, equity options, and comprehensive benefits</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="mr-4 text-primary-600 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold">Flexible Work Environment</h4>
                  <p className="text-dark-500">Remote-friendly policies with beautiful office spaces when you need them</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="mr-4 text-primary-600 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold">Growth Opportunities</h4>
                  <p className="text-dark-500">Professional development, continuous learning, and career advancement</p>
                </div>
              </div>
            </div>
            <Button
              variant="primary"
              to="/careers"
              className="relative overflow-hidden group"
            >
              <span className="relative z-10">Explore Open Positions</span>
              <span className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CareerHighlight;